import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import { createInspectionReport } from '#/api/imsMgmt/inspectionReport/create';
import { fetchMaterialSn } from '#/api/imsMgmt/inspectionReport/fetchMaterialSn';
import { voidInspectionReport, voidMaterialSn } from '#/api/imsMgmt/inspectionReport/voidReport';
import { fetchInspectionKindDetail } from '#/api/imsMgmt/settings/inspectionKind/fetchDetail';
import { fetchInspectionKindList } from '#/api/imsMgmt/settings/inspectionKind/fetchList';
import { getCustomerPortalSnUrl } from '#/config/customerPortal';
import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import FloatingSelect from '#/modules/commonComponents/form/select/FloatingSelect';
import PageShell from '#/modules/infra/layout/PageShell';
import {
  INSPECTION_STATUS_LABELS,
  getInspectionReportApiErrorMessage,
} from '#/modules/imsMgmt/inspectionReport/helper/apiError';
import {
  RESULT_OPTIONS,
  buildInspectionReportPayload,
} from '#/modules/imsMgmt/inspectionReport/helper/buildInspectionReportPayload';
import { inspectionKindDetailQueryKey, inspectionKindListQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const MODE_OPTIONS = [
  { value: 'first', label: '首次登錄' },
  { value: 'retest', label: '重測' },
];

const RESULT_SELECT_OPTIONS = RESULT_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));

function allowFormSubmitOnEnter() {
  return undefined;
}

function ReportList({ reports, onVoid }) {
  if (!reports?.length) {
    return <p className="mt-3 text-sm text-muted-foreground">沒有報告</p>;
  }
  return (
    <ul className="m-0 mt-3 list-none p-0 gap-3 flex flex-col">
      {reports.map((report) => (
        <li
          key={report.id}
          className="p-3 text-sm border border-border rounded-md"
        >
          <p className="mb-0">
            {report.kindName || report.kindCode || '報告'}
            {' · '}
            {report.status || '—'}
            {' · '}
            {report.overallResult || '—'}
          </p>
          {report.status === 'EFFECTIVE' ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => onVoid(report.id)}
            >
              作廢這筆報告
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

ReportList.propTypes = {
  reports: PropTypes.arrayOf(PropTypes.object),
  onVoid: PropTypes.func.isRequired,
};

const MaterialSnPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSerial = searchParams.get('serial') ?? '';
  const [serialInput, setSerialInput] = useState(initialSerial);
  const [lookupSerial, setLookupSerial] = useState(initialSerial);
  const {
    register,
    control,
    getValues,
    setValue,
    handleSubmit,
  } = useForm({
    defaultValues: {
      mode: 'first',
      mtrId: '',
      snMappingId: '',
      workOrderId: '',
      kindId: '',
      overallResult: 'PASS',
      remark: '',
      entries: {},
    },
  });
  const mode = useWatch({ control, name: 'mode' });
  const kindId = useWatch({ control, name: 'kindId' });

  const historyQuery = useQuery({
    queryKey: ['erp', 'materialSn', lookupSerial],
    queryFn: () => fetchMaterialSn(lookupSerial),
    enabled: Boolean(lookupSerial),
    retry: false,
  });

  const kindListQuery = useQuery({
    queryKey: inspectionKindListQueryKey(),
    queryFn: fetchInspectionKindList,
  });

  const kindQuery = useQuery({
    queryKey: inspectionKindDetailQueryKey(kindId),
    queryFn: () => fetchInspectionKindDetail(kindId),
    enabled: Boolean(kindId),
  });

  const history = historyQuery.data?.data;
  const registered = history?.materialSn?.status === 'REGISTERED';
  const activeItems = useMemo(
    () => (kindQuery.data?.data?.items ?? []).filter((item) => item?.status !== 'DEPRECATED'),
    [kindQuery.data],
  );
  const kindOptions = useMemo(
    () => (kindListQuery.data?.data ?? [])
      .filter((kind) => kind.status === 'ACTIVE')
      .map((kind) => ({
        value: kind.id,
        label: [kind.code, kind.name].filter(Boolean).join(' '),
      })),
    [kindListQuery.data],
  );

  useEffect(() => {
    activeItems.forEach((item) => {
      if (!getValues(`entries.${item.id}.result`)) {
        setValue(`entries.${item.id}.result`, 'PASS');
      }
    });
  }, [activeItems, getValues, setValue]);

  const saveMutation = useMutation({
    mutationFn: (values) => createInspectionReport(buildInspectionReportPayload({
      mode: registered ? 'retest' : values.mode,
      serialNumber: serialInput,
      mtrId: values.mtrId,
      snMappingId: values.snMappingId,
      workOrderId: values.workOrderId,
      kind: kindQuery.data?.data,
      entries: values.entries,
      overallResult: values.overallResult,
      remark: values.remark,
    })),
    onSuccess: (result) => {
      toast.success(result?.message || '檢測報告已建立');
      const nextSerial = serialInput;
      setLookupSerial(nextSerial);
      setSearchParams(nextSerial ? { serial: nextSerial } : {});
    },
    onError: (error) => {
      toast.error(getInspectionReportApiErrorMessage(error, '建立檢測報告失敗'));
    },
  });

  const voidMutation = useMutation({
    mutationFn: ({ type, id }) => {
      const reason = window.prompt('作廢原因');
      if (!reason || !reason.trim()) {
        return Promise.reject(new Error('請填寫作廢原因'));
      }
      const payload = { voidReason: reason.trim() };
      return type === 'registry'
        ? voidMaterialSn(id, payload)
        : voidInspectionReport(id, payload);
    },
    onSuccess: (result) => {
      toast.success(result?.message || '已作廢');
      historyQuery.refetch();
    },
    onError: (error) => {
      toast.error(error?.message || getInspectionReportApiErrorMessage(error, '作廢失敗'));
    },
  });

  function handleLookup(event) {
    event.preventDefault();
    setLookupSerial(serialInput);
    setSearchParams(serialInput ? { serial: serialInput } : {});
  }

  function onSubmitReport(values) {
    if (saveMutation.isPending || !values.kindId) {
      return;
    }
    saveMutation.mutate(values);
  }

  const customerPortalUrl = history?.materialSn?.status === 'REGISTERED'
    ? getCustomerPortalSnUrl(history.materialSn.serialNumber)
    : null;

  const statusLabel = INSPECTION_STATUS_LABELS[history?.inspectionStatus]
    || history?.inspectionStatus
    || '—';

  return (
    <PageShell variant="formTall">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">序號檢測履歷</span>
            </h1>
          </div>

          <form className="sheet py-4" onSubmit={handleLookup}>
            <div className="gap-3 flex flex-col sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <FloatingInput
                  id="serialNumber"
                  label="完整序號"
                  value={serialInput}
                  onChange={(event) => setSerialInput(event.target.value)}
                  onKeyDown={allowFormSubmitOnEnter}
                />
              </div>
              <Button type="submit" className="shrink-0">查詢</Button>
            </div>
          </form>

          {historyQuery.isError ? (
            <p className="mt-4 text-sm text-destructive">
              {getInspectionReportApiErrorMessage(historyQuery.error, '查不到此序號')}
            </p>
          ) : null}

          <div className="mt-4 gap-4 grid grid-cols-1 items-start lg:grid-cols-2">
            <div className="sheet create_edit create_edit_form create_edit_form--page-scroll">
              <div className="create_edit_form__body">
                <div className="form-section w-full">
                  <div className="form-section__header">
                    <h2 className="form-block__title mb-0!">檢測履歷</h2>
                  </div>
                  {history ? (
                    <>
                      <p className="mt-3 mb-0 text-sm text-muted-foreground">
                        {`資格：${statusLabel}（不是銷售或出貨狀態）`}
                      </p>
                      <p className="mt-1 mb-0 text-sm text-muted-foreground">
                        {`登錄狀態：${history.materialSn?.status || '—'}`}
                        {history.materialSn?.productName || history.materialSn?.productCode
                          ? ` · ${history.materialSn.productName || history.materialSn.productCode}`
                          : ''}
                      </p>
                      {customerPortalUrl ? (
                        <p className="mt-1 mb-0 text-sm">
                          <a
                            href={customerPortalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline-offset-2 hover:underline"
                          >
                            在客戶站查看產品履歷
                          </a>
                        </p>
                      ) : null}
                      {(history.inspections ?? []).map((inspection) => (
                        <div key={inspection.id} className="mt-3">
                          <p className="mb-0 text-sm font-medium">
                            {inspection.name || inspection.code}
                            {' · 目前 '}
                            {inspection.currentResult || inspection.currentReason || '—'}
                          </p>
                          <ReportList
                            reports={inspection.reports}
                            onVoid={(id) => voidMutation.mutate({ type: 'report', id })}
                          />
                        </div>
                      ))}
                      {history.materialSn?.status === 'REGISTERED' ? (
                        <div className="mt-3">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => voidMutation.mutate({
                              type: 'registry',
                              id: history.materialSn.id,
                            })}
                          >
                          作廢此序號登錄
                          </Button>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="form-section__empty-state mt-3">
                      {historyQuery.isFetching ? '查詢中…' : '輸入完整序號後查詢'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <form
              className="sheet create_edit create_edit_form create_edit_form--page-scroll"
              onSubmit={handleSubmit(onSubmitReport)}
              noValidate
            >
              <div className="create_edit_form__body">
                <div className="form-section w-full">
                  <div className="form-section__header">
                    <h2 className="form-block__title mb-0!">
                      {registered ? '重測' : '首次登錄'}
                    </h2>
                  </div>
                  <p className="mt-2 mb-0 text-sm text-muted-foreground">
                    人工檢測不送檢測時間。重測不會改序號的物料、對照或工單。
                  </p>
                </div>
                {registered ? null : (
                  <FloatingSelect
                    id="mode"
                    label="建立方式"
                    options={MODE_OPTIONS}
                    control={control}
                  />
                )}
                {registered ? null : (
                  <FloatingInput
                    id="mtrId"
                    label="物料 ID"
                    register={register}
                  />
                )}
                {!registered && mode === 'first' ? (
                  <FloatingInput
                    id="snMappingId"
                    label="物料序號對照 ID"
                    register={register}
                  />
                ) : null}
                {!registered && mode === 'first' ? (
                  <FloatingInput
                    id="workOrderId"
                    label="工單 ID（可空白）"
                    register={register}
                  />
                ) : null}
                <FloatingSelect
                  id="kindId"
                  label="檢測種類"
                  options={kindOptions}
                  control={control}
                  allowEmpty
                />
                <div className="form-section @container/form-section w-full">
                  <div className="form-section__header">
                    <h2 className="form-block__title mb-0!">條件項目</h2>
                  </div>
                  {activeItems.length === 0 ? (
                    <p className="mt-3 mb-0 text-sm text-muted-foreground">請先選擇檢測種類。</p>
                  ) : activeItems.map((item) => (
                    <div
                      key={item.id}
                      className="mt-3 p-3 border border-border rounded-md"
                    >
                      <h3 className="mb-3 text-sm font-medium">
                        {item.itemName || item.itemCode}
                      </h3>
                      <div className="gap-3 grid grid-cols-1 md:grid-cols-2">
                        <PlainRegisteredInput
                          id={`entries.${item.id}.measuredValue`}
                          register={register}
                          mobileLabel="量測值"
                          ariaLabel="量測值"
                        />
                        <FloatingSelect
                          id={`entries.${item.id}.result`}
                          label="項目結果"
                          options={RESULT_SELECT_OPTIONS}
                          control={control}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <FloatingSelect
                  id="overallResult"
                  label="整張結果"
                  options={RESULT_SELECT_OPTIONS}
                  control={control}
                />
                <FloatingTextarea
                  id="remark"
                  label="備註"
                  register={register}
                  rows={2}
                />
              </div>
              <div className="create_edit_form__actions mt-4 gap-2 flex flex-wrap justify-end">
                <Button type="submit" disabled={saveMutation.isPending || !kindId}>
                  {saveMutation.isPending ? '送出中…' : '送出報告'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default MaterialSnPage;
